const DRAG_THRESHOLD_PX = 8;

export type BoxRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

// ponytail: WeakMap cache — rebuilds when GeoJSON features are replaced (new object identity).
const featureBoundsCache = new WeakMap<
  google.maps.Data.Feature,
  google.maps.LatLngBounds
>();

export function getDistance(x1: number, y1: number, x2: number, y2: number) {
  return Math.hypot(x2 - x1, y2 - y1);
}

export { DRAG_THRESHOLD_PX };

export function getFeatureBounds(feature: google.maps.Data.Feature): google.maps.LatLngBounds | null {
  const cached = featureBoundsCache.get(feature);
  if (cached) return cached;

  const geometry = feature.getGeometry();
  if (!geometry) return null;

  const bounds = new google.maps.LatLngBounds();
  geometry.forEachLatLng((latLng) => bounds.extend(latLng));

  if (bounds.isEmpty()) return null;
  featureBoundsCache.set(feature, bounds);
  return bounds;
}

export function featureIntersectsBounds(
  feature: google.maps.Data.Feature,
  selectionBounds: google.maps.LatLngBounds
): boolean {
  const geometry = feature.getGeometry();
  if (!geometry) return false;

  const featureBounds = getFeatureBounds(feature);
  if (!featureBounds || !selectionBounds.intersects(featureBounds)) {
    return false;
  }

  let vertexInside = false;
  geometry.forEachLatLng((latLng) => {
    if (selectionBounds.contains(latLng)) {
      vertexInside = true;
    }
  });

  return vertexInside || selectionBounds.intersects(featureBounds);
}

export function featureContainedInBounds(
  feature: google.maps.Data.Feature,
  selectionBounds: google.maps.LatLngBounds
): boolean {
  const featureBounds = getFeatureBounds(feature);
  if (!featureBounds) return false;
  return (
    selectionBounds.contains(featureBounds.getNorthEast()) &&
    selectionBounds.contains(featureBounds.getSouthWest())
  );
}

export function getFeaturesInBounds(
  map: google.maps.Map,
  selectionBounds: google.maps.LatLngBounds
): google.maps.Data.Feature[] {
  const features: google.maps.Data.Feature[] = [];

  map.data.forEach((feature) => {
    const id = feature.getProperty("id");
    if (!id) return;
    if (featureIntersectsBounds(feature, selectionBounds)) {
      features.push(feature);
    }
  });

  return features;
}

export function getFeaturesContainedInBounds(
  map: google.maps.Map,
  selectionBounds: google.maps.LatLngBounds
): google.maps.Data.Feature[] {
  const features: google.maps.Data.Feature[] = [];

  map.data.forEach((feature) => {
    const id = feature.getProperty("id");
    if (!id) return;
    if (featureContainedInBounds(feature, selectionBounds)) {
      features.push(feature);
    }
  });

  return features;
}

export function getIndexedFeaturesContainedInBounds(
  featuresById: Map<string, google.maps.Data.Feature>,
  selectionBounds: google.maps.LatLngBounds
): google.maps.Data.Feature[] {
  const features: google.maps.Data.Feature[] = [];

  featuresById.forEach((feature) => {
    if (featureContainedInBounds(feature, selectionBounds)) {
      features.push(feature);
    }
  });

  return features;
}

/** Bounds pré-calculados — hit test sem geometry do Google Maps. */
export type TalhaoBounds = {
  neLat: number;
  neLng: number;
  swLat: number;
  swLng: number;
};

type GeoJsonPolygonFeature = {
  properties?: { id?: unknown };
  geometry: {
    type: string;
    coordinates: number[][][] | number[][][][] ;
  };
};

export function boundsFromGeoJsonFeature(
  feature: GeoJsonPolygonFeature
): TalhaoBounds | null {
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  const extend = (lng: number, lat: number) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  };

  const { type, coordinates } = feature.geometry;
  if (type === "Polygon") {
    for (const coord of coordinates[0] as number[][]) {
      extend(coord[0], coord[1]);
    }
  } else if (type === "MultiPolygon") {
    for (const poly of coordinates as number[][][][]) {
      for (const coord of poly[0]) {
        extend(coord[0], coord[1]);
      }
    }
  } else {
    return null;
  }

  if (!Number.isFinite(minLat)) return null;
  return { neLat: maxLat, neLng: maxLng, swLat: minLat, swLng: minLng };
}

export function buildTalhaoBoundsIndex(
  features: GeoJsonPolygonFeature[]
): Map<string, TalhaoBounds> {
  const index = new Map<string, TalhaoBounds>();
  for (const feature of features) {
    const id = feature.properties?.id;
    if (id == null || id === "") continue;
    const bounds = boundsFromGeoJsonFeature(feature);
    if (bounds) index.set(String(id), bounds);
  }
  return index;
}

export function latLngBoundsToTalhaoBounds(
  bounds: google.maps.LatLngBounds
): TalhaoBounds {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  return {
    neLat: ne.lat(),
    neLng: ne.lng(),
    swLat: sw.lat(),
    swLng: sw.lng(),
  };
}

export function boundsContainedInSelection(
  talhao: TalhaoBounds,
  selection: TalhaoBounds
): boolean {
  return (
    selection.swLat <= talhao.swLat &&
    selection.neLat >= talhao.neLat &&
    selection.swLng <= talhao.swLng &&
    selection.neLng >= talhao.neLng
  );
}

export function getContainedTalhaoIds(
  boundsById: Map<string, TalhaoBounds>,
  selectionBounds: google.maps.LatLngBounds
): string[] {
  const selection = latLngBoundsToTalhaoBounds(selectionBounds);
  const ids: string[] = [];
  boundsById.forEach((talhaoBounds, id) => {
    if (boundsContainedInSelection(talhaoBounds, selection)) {
      ids.push(id);
    }
  });
  return ids;
}

export function pixelBoundsToLatLngBounds(
  projection: google.maps.MapCanvasProjection,
  rect: BoxRect
): google.maps.LatLngBounds | null {
  const nw = projection.fromContainerPixelToLatLng(
    new google.maps.Point(rect.left, rect.top)
  );
  const se = projection.fromContainerPixelToLatLng(
    new google.maps.Point(rect.left + rect.width, rect.top + rect.height)
  );

  if (!nw || !se) return null;

  const bounds = new google.maps.LatLngBounds();
  bounds.extend(nw);
  bounds.extend(se);
  return bounds;
}

export function clientRectToContainerRect(
  container: HTMLElement,
  clientX: number,
  clientY: number,
  originClientX: number,
  originClientY: number
): BoxRect {
  const containerRect = container.getBoundingClientRect();
  const left = Math.min(originClientX, clientX) - containerRect.left;
  const top = Math.min(originClientY, clientY) - containerRect.top;
  const width = Math.abs(clientX - originClientX);
  const height = Math.abs(clientY - originClientY);
  return { left, top, width, height };
}

export function clientRectToMapDivRect(
  mapDiv: HTMLElement,
  clientX: number,
  clientY: number,
  originClientX: number,
  originClientY: number
): BoxRect {
  const mapRect = mapDiv.getBoundingClientRect();
  const left = Math.min(originClientX, clientX) - mapRect.left;
  const top = Math.min(originClientY, clientY) - mapRect.top;
  const width = Math.abs(clientX - originClientX);
  const height = Math.abs(clientY - originClientY);
  return { left, top, width, height };
}
